import { View, Text, Pressable, Alert, ScrollView, Platform } from 'react-native';
import ProjectGantt from './Components/ProjectGantt';
import { YStack, Progress, Spinner, XStack, Button as TamaguiButton, Sheet } from 'tamagui';
import styles from '@stylesheet';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AntDesign, Foundation, Entypo, MaterialIcons, Feather } from '@expo/vector-icons';
import Modal from 'react-native-modal';
import apis from '@api/api';
import * as Animatable from 'react-native-animatable';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import AddTeamMember from './Components/AddTeamMember';
import Button from '@components/UI/Button/Button';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AddTaskModal, { AddTaskModalRef } from './Components/AddTask';
import AddWorkPackage, { AddWorkPackageRef } from './Components/AddWorkPackage';
import toTitleCase from '@hooks/toTitleCase';
import { ToolbarContext } from '@hooks/toolbarContext';
import InputFieldStyled from '@components/Global/InputFieldStyled';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import DraggableFlatList from 'react-native-draggable-flatlist';
import { set } from 'lodash';
import { H2, H3, H4, H5, Card, Separator } from 'tamagui';
import { CONTEXT_SIZE_TOGGLE, OPEN_CONTEXT } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import RenderWorkPackage from './Components/RenderWorkPackage';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('DraggableFlatList Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={{ padding: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
            Something went wrong with the list
          </Text>
          <Button 
            buttonText="Reload" 
            onPress={() => this.setState({ hasError: false, error: null })} 
          />
        </View>
      );
    }

    return this.props.children;
  }
}

// Debug logging utility - reduced to prevent crashes
const debugLog = (component: string, action: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 [IndividualProjectPage] ${component} - ${action}`);
  }
};

const IndividualProjectPage = ({ route, navigation }) => {
  debugLog('Component', 'Starting initialization');
  
  try {
    const { projectId, title } = route?.params;
    debugLog('RouteParams', 'Extracted', { projectId, title });
    const wpRef = useRef<AddWorkPackageRef>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [projectGantt, setProjectGantt] = useState(true);
    const [retrievedProject, setRetrievedProject] = useState([]);
    const [projectParticipants, setProjectParticipants] = useState([]);
    const [projectOwner, setProjectOwner] = useState();
    debugLog('State', 'Basic state initialized');
    
    const userId = useGlobalUser();
    debugLog('GlobalUser', 'Retrieved', { userId });
    
    const [project, setProject] = useState({});
    const theme = themeSelector();
    debugLog('Theme', 'Retrieved', { theme });
    
    const scrollRef = useRef(null);
    const taskRef = useRef<AddTaskModalRef>(null);
    debugLog('Refs', 'Initialized');
    
    const { setProjectContext } = useContext(ToolbarContext);
    debugLog('ToolbarContext', 'Retrieved');
    
    const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
    const dispatch = useDispatch();
    debugLog('Redux', 'Dispatch retrieved');

    // State for work packages - prevent memory leaks
    const [retrievedWorkPackages, setRetrievedWorkPackages] = useState([]);
    const [highlightedItem, setHighlightedItem] = useState(null);
    debugLog('WorkPackages', 'State initialized');

    // Modern UI state management
    const [showActionSheet, setShowActionSheet] = useState(false);
    const [viewMode, setViewMode] = useState<'kanban' | 'gantt' | 'list' | 'booking'>('kanban');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed'>('all');
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    debugLog('ModernUI', 'State initialized');

  // Memoized callback to prevent re-creation on every render
  const _addWP = useCallback((wp) => {
    debugLog('AddWP', 'Adding work package', { wpId: wp?._id });
    setRetrievedWorkPackages((prev) => [...prev, wp]);
  }, []);

  // Stable query function to prevent infinite re-renders
    const queryFn = useCallback(async () => {
    try {
      const data = await apis.getProject(projectId, userId);
      const projectData = data.data.data || data.data || {};
      const workPackages = projectData?.work_packages || [];
      const team = projectData?.team || [];
      
      // Update all related state
      setRetrievedWorkPackages(workPackages);
      setProjectParticipants(team);
      setHighlightedItem(workPackages[0] || null);
      setProjectContext({ project: projectData, updateFn: _addWP });
      
      return data.data;
    } catch (error) {
      console.error('API call failed:', error.message);
      throw error;
    }
    }, [projectId, userId, setProjectContext, _addWP]);

  const { status, data: projectData, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['activeProject', projectId], // Add projectId to make it unique
    queryFn,
    retry: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (updated property name)
  });

  debugLog('ReactQuery', 'Query state', { status, isLoading, isFetching, hasError: !!error });

  // Type-safe data access
  const projectDataTyped = projectData as any;

  // Memoized key extractor to prevent memory leaks
  const keyExtractor = useCallback((item: any, index: number) => {
    const key = item?._id || `item-${index}`;
    debugLog('KeyExtractor', 'Generated key', { key, itemId: item?._id, index });
    return key;
  }, []);

  // Memoized render item to prevent unnecessary re-renders
  const renderWorkPackageItem = useCallback(({ item, isActive, drag }) => {
    // Safety check for item
    if (!item || !item._id) {
      return <View style={{ height: 50, backgroundColor: 'red', margin: 5 }} />;
    }

    return (
      <View style={{
        width: 380,
        minHeight: 280,
        margin: 10,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: isActive ? 0.3 : 0.1,
        shadowRadius: isActive ? 30 : 20,
        elevation: isActive ? 8 : 3,
        borderWidth: isActive ? 2 : 1,
        borderColor: isActive ? (theme ? '#3B82F6' : '#60A5FA') : (theme ? '#E5E7EB' : '#4B5563'),
        overflow: 'hidden',
        transform: isActive ? [{ scale: 1.05 }] : [{ scale: 1 }],
        opacity: isActive ? 0.9 : 1
      }}>
        <RenderWorkPackage 
          setRetrievedWorkPackages={setRetrievedWorkPackages} 
          setHighlightedItem={setHighlightedItem} 
          item={item} 
          refresh={refetch} 
          drag={drag} 
          isActive={isActive} 
          projectGantt={projectGantt}
          projectId={projectId}
          highlightedItem={highlightedItem}
          taskRef={taskRef}
        />
      </View>
    );
  }, [refetch, projectGantt, projectId, highlightedItem, theme]);

  // Cleanup function
    useEffect(() => {
      return () => {
      // Clear any pending state updates
      setRetrievedWorkPackages([]);
        setHighlightedItem(null);
    };
    }, []);

  // Add effect to handle projectId changes and prevent stale data
    useEffect(() => {
    setRetrievedWorkPackages([]);
    setHighlightedItem(null);
    setProjectGantt(true);
  }, [projectId]);

  // Memoized handlers for better performance - prevent memory leaks
  const handlePreviousItem = useCallback(() => {
    debugLog('Navigation', 'Previous item requested');
    if (!retrievedWorkPackages.length || !highlightedItem) {
      debugLog('Navigation', 'Previous item - no packages or highlighted item');
      return;
    }
    
    const currentIndex = retrievedWorkPackages.findIndex((wp) => wp._id === highlightedItem._id);
    debugLog('Navigation', 'Previous item - current index', { currentIndex });
    
    if (currentIndex <= 0) {
      debugLog('Navigation', 'Previous item - already at first item');
      return;
    }
    
    const newItem = retrievedWorkPackages[currentIndex - 1];
    debugLog('Navigation', 'Previous item - setting new item', { newItemId: newItem?._id });
    setHighlightedItem(newItem);
    
    try {
      // Add bounds checking to prevent crashes
      if (scrollRef.current && currentIndex > 0 && currentIndex <= retrievedWorkPackages.length) {
        scrollRef.current.scrollToIndex({ 
          index: currentIndex - 1, 
          animated: true,
          viewOffset: 0,
          viewPosition: 0.5 
        });
        debugLog('Navigation', 'Previous item - scroll successful');
      }
    } catch (error) {
      debugLog('Navigation', 'Previous item - scroll failed', { error: error.message });
      console.warn('Scroll to index failed:', error);
      // Fallback: try scrolling to offset
      try {
        scrollRef.current?.scrollToOffset({ 
          offset: (currentIndex - 1) * 300, 
          animated: true 
        });
      } catch (fallbackError) {
        debugLog('Navigation', 'Fallback scroll failed', { error: fallbackError.message });
      }
    }
  }, [retrievedWorkPackages, highlightedItem]);

  const handleNextItem = useCallback(() => {
    debugLog('Navigation', 'Next item requested');
    if (!retrievedWorkPackages.length || !highlightedItem) {
      debugLog('Navigation', 'Next item - no packages or highlighted item');
      return;
    }
    
    const currentIndex = retrievedWorkPackages.findIndex((wp) => wp._id === highlightedItem._id);
    debugLog('Navigation', 'Next item - current index', { currentIndex });
    
    if (currentIndex >= retrievedWorkPackages.length - 1 || currentIndex === -1) {
      debugLog('Navigation', 'Next item - already at last item or item not found');
      return;
    }
    
    const newItem = retrievedWorkPackages[currentIndex + 1];
    debugLog('Navigation', 'Next item - setting new item', { newItemId: newItem?._id });
    setHighlightedItem(newItem);
    
    try {
      // Add bounds checking to prevent crashes
      if (scrollRef.current && currentIndex >= 0 && currentIndex < retrievedWorkPackages.length - 1) {
        scrollRef.current.scrollToIndex({ 
          index: currentIndex + 1, 
          animated: true,
          viewOffset: 0,
          viewPosition: 0.5 
        });
        debugLog('Navigation', 'Next item - scroll successful');
      }
    } catch (error) {
      debugLog('Navigation', 'Next item - scroll failed', { error: error.message });
      console.warn('Scroll to index failed:', error);
      // Fallback: try scrolling to offset
      try {
        scrollRef.current?.scrollToOffset({ 
          offset: (currentIndex + 1) * 300, 
          animated: true 
        });
      } catch (fallbackError) {
        debugLog('Navigation', 'Fallback scroll failed', { error: fallbackError.message });
      }
    }
  }, [retrievedWorkPackages, highlightedItem]);

  // Memoized handlers for better performance
  const handleShowGantt = useCallback(() => {
    debugLog('Gantt', 'Toggle gantt view', { current: projectGantt, new: !projectGantt });
    setProjectGantt(!projectGantt)
  }, [projectGantt]);

  // Modern interaction handlers
    const handleViewModeChange = useCallback((mode: 'kanban' | 'gantt' | 'list' | 'booking') => {
    debugLog('ViewMode', 'Changing view mode', { mode });
    setViewMode(mode);
    setProjectGantt(mode === 'gantt');
  }, []);

  const handleFilterChange = useCallback((filter: 'all' | 'active' | 'completed') => {
    debugLog('Filter', 'Changing filter', { filter });
    setFilterStatus(filter);
  }, []);

  const handleShareProject = useCallback(() => {
    debugLog('Share', 'Opening share modal');
    setShowShareModal(true);
  }, []);

  const handleAddTeamMember = useCallback(() => {
    debugLog('Team', 'Opening team modal');
    setShowTeamModal(true);
  }, []);

  const handleExportProject = useCallback(() => {
    debugLog('Export', 'Exporting project');
    Alert.alert(
      'Export Project',
      'Choose export format:',
      [
        { text: 'PDF Report', onPress: () => console.log('Export PDF') },
        { text: 'Excel', onPress: () => console.log('Export Excel') },
        { text: 'JSON', onPress: () => console.log('Export JSON') },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  }, []);

  const handleProjectSettings = useCallback(() => {
    debugLog('Settings', 'Opening project settings');
    setShowActionSheet(true);
  }, []);

  // Memoized calculations
  const isLargeScreen = useMemo(() => {
    const result = pageDimensions.width > 1600;
    debugLog('Layout', 'Screen size check', { width: pageDimensions.width, isLargeScreen: result });
    return result;
  }, [pageDimensions.width]);
  
  const maxHeightValue = useMemo(() => {
    const value = projectGantt ? (isLargeScreen ? "100%" : '28%') : "100%";
    debugLog('Layout', 'Max height calculated', { projectGantt, isLargeScreen, maxHeightValue: value });
    return value;
  }, [projectGantt, isLargeScreen]);


    // Separate each component for easy debugging
    const addTaskModalComponent = (
      <AddTaskModal
        onClose={() => { 
          refetch();
        }}
        ref={taskRef} 
        projectTeam={projectParticipants || []}
        workPackageList={retrievedWorkPackages || []}
        projectId={projectId} 
        projectTitle={projectDataTyped?.data?.title || title || ''}
        selectedWP={{
          work_package_title: '',
          _id: ''
        }}
        taskAssign={undefined}
        currentlyAssigned={undefined}
      />
    );

    const loadingComponent = (
      <View style={[
        styles.centerEverything,
        {
          alignSelf: "center",
          gap: 15,
          padding: 50,
          justifyContent: "center",
          flex: 1,
          borderRadius: 20,
          shadowRadius: 20,
          shadowOpacity: .2,
          margin: 150,
          maxHeight: 350,
          backgroundColor: theme ? '#ffffff' : '#374151'
        }
      ]}>
        <Text style={{
          flex: 1,
          textAlign: "center",
          color: theme ? '#1F2937' : '#F9FAFB',
          fontSize: 16,
          fontWeight: '500'
        }}>
          Loading {toTitleCase(title)}
        </Text>
        <Spinner />
      </View>
    );

    const ganttNavigationComponent = projectGantt && (
      <View style={{ flex: 1.9, width: 500, flexDirection: "row" }}>
        <AntDesign
          style={{ alignSelf: "center", justifyContent: "center" }}
          name="leftsquare"
          size={24}
          color={"rgb(90,192,234)"}
          onPress={handlePreviousItem}
        />
        {highlightedItem ? (
          <RenderWorkPackage
            taskRef={taskRef}
            setRetrievedWorkPackages={setRetrievedWorkPackages}
            item={highlightedItem}
            refresh={refetch}
            drag={() => { }}
            isActive={false}
            projectGantt={false}
            projectId={projectId}
            highlightedItem={highlightedItem}
            setHighlightedItem={setHighlightedItem}
          />
        ) : (
          <View style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme ? '#ffffff' : '#374151',
            borderRadius: 12,
            margin: 10
          }}>
            <Text style={{ color: theme ? '#6B7280' : '#9CA3AF' }}>
              No work package selected
            </Text>
          </View>
        )}
        <AntDesign
          style={{ alignSelf: "center", justifyContent: "center", marginLeft: 20 }}
          name="rightsquare"
          size={24}
          color="rgb(90,192,234)"
          onPress={handleNextItem}
        />
      </View>
    );

    const projectGanttComponent = projectGantt && projectDataTyped?.data && (
      <View style={{
        flex: 4,
        minHeight: "65%",
        padding: 40,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 16,
        marginBottom: 30,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3
      }}>
        <AntDesign
          name="eyeo"
          size={24}
          color={theme ? "black" : "white"}
          style={{ position: "absolute", top: 20, right: 45, zIndex: 100 }}
          onPress={handleShowGantt}
        />
        <ProjectGantt
          setHighlightedItem={setHighlightedItem}
          highlightedItem={retrievedWorkPackages?.findIndex((wp) => wp?._id === highlightedItem?._id) || 0}
          ref={scrollRef} 
          users={projectParticipants || []}
          taskList={retrievedWorkPackages || []} 
          projectStart={projectDataTyped?.data?.start_date || new Date().toISOString()}
          projectEnd={projectDataTyped?.data?.end_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()}
        />
      </View>
    );

    const workPackageListComponent = (
      <Animatable.View
        transition={['flex', 'maxHeight']}
        style={{
          top: projectGantt ? -20 : 0,
          minWidth: 400,
          maxHeight: maxHeightValue,
          flex: projectGantt ? 3 : 10,
          backgroundColor: theme ? '#ffffff' : '#374151',
          borderRadius: 16,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 3,
          borderWidth: 1,
          borderColor: theme ? '#E5E7EB' : '#4B5563'
        }}
      >
        {Array.isArray(retrievedWorkPackages) ? (
          <ErrorBoundary>
            <DraggableFlatList
              data={retrievedWorkPackages}
              renderItem={renderWorkPackageItem}
              style={{ paddingVertical: projectGantt ? 0 : 5 }}
              ref={scrollRef}
              onRelease={(params) => {
                console.log('Drag released:', params);
              }}
              onDragBegin={(params) => {
                console.log('Drag began:', params);
              }}
              onDragEnd={({ data }) => {
                console.log('Drag ended, new order:', data);
                if (data && Array.isArray(data)) {
                  setRetrievedWorkPackages(data);

                  // Update work package order in backend
                  const updateWorkPackageOrder = async () => {
                    try {
                      // TODO: Implement backend API for work package reordering
                      // await apis.updateWorkPackageOrder(projectId, data.map((wp, index) => ({
                      //   workPackageId: wp._id,
                      //   order: index
                      // })));
                      console.log('Work package order updated locally');
                    } catch (error) {
                      console.error('Failed to update work package order:', error);
                    }
                  };

                  updateWorkPackageOrder();
                }
              }}
              CellRendererComponent={({ children, style, ...rest }) => (
                <View style={[style, { flex: 1 }]} {...rest}>
                  {children}
                </View>
              )}
              keyExtractor={keyExtractor}
              contentContainerStyle={{
                paddingLeft: 40,
                alignSelf: "stretch",
                paddingVertical: retrievedWorkPackages?.length ? 10 : 20,
                paddingBottom: 30,
                paddingRight: 20
              }}
              ListFooterComponent={() => (
                <View style={[
                  styles.centerEverything,
                  {
                    margin: 10,
                    gap: 20,
                    justifyContent: "center",
                    padding: 40,
                    width: 400,
                    minHeight: 300,
                    overflow: 'visible',
                    borderRadius: 20,
                    backgroundColor: theme ? '#ffffff' : '#374151',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 20,
                    elevation: 3,
                    paddingHorizontal: 50,
                    borderWidth: 2,
                    borderColor: theme ? '#E5E7EB' : '#4B5563',
                    borderStyle: 'dashed'
                  }
                ]}>
                  <View style={{
                    padding: 16,
                    borderRadius: 50,
                    backgroundColor: theme ? '#3B82F6' : '#60A5FA',
                    opacity: 0.2,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AntDesign
                      name="plus"
                      size={32}
                      color={theme ? '#3B82F6' : '#60A5FA'}
                    />
                  </View>
                  <Text style={{
                    color: theme ? '#1F2937' : '#F9FAFB',
                    fontSize: 20,
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    {retrievedWorkPackages.length > 0 ? 'Add Another Work Package' : 'Create Your First Work Package'}
                  </Text>
                  <Text style={{
                    fontStyle: "italic",
                    textAlign: "center",
                    color: theme ? '#6B7280' : '#9CA3AF',
                    fontSize: 14,
                    lineHeight: 20,
                    maxWidth: 280
                  }}>
                    {retrievedWorkPackages.length > 0 
                      ? 'Continue building your project by adding more work packages' 
                      : 'Work packages help organize your project tasks and deliverables'
                    }
                  </Text>
                  <Button
                    buttonText="Add Work Package"
                    onPress={() => wpRef.current?.openModal()}
                    customStyle={{
                      backgroundColor: theme ? '#3B82F6' : '#60A5FA',
                      borderRadius: 12,
                      paddingHorizontal: 24,
                      paddingVertical: 12,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 2
                    }}
                  />
                </View>
              )}
              horizontal={true}
              alwaysBounceHorizontal
              nestedScrollEnabled
              showsHorizontalScrollIndicator
              removeClippedSubviews={true}
              maxToRenderPerBatch={10}
              windowSize={5}
              initialNumToRender={5}
              getItemLayout={undefined}
              dragItemOverflow={true}
              activationDistance={10}
              dragHitSlop={{ left: -10, right: -10, top: -10, bottom: -10 }}
              onScrollToIndexFailed={(info) => {
                setTimeout(() => {
                  try {
                    scrollRef.current?.scrollToOffset({
                      offset: info.index * 300,
                      animated: true
                    });
                  } catch (error) {
                  console.warn('Fallback scroll failed:', error);
                }
              }, 100);
              }}
            />
          </ErrorBoundary>
        ) : (
          <View style={{ 
            padding: 40, 
            alignItems: 'center', 
            flex: 1, 
            justifyContent: 'center',
            gap: 20
          }}>
            <View style={{
              padding: 16,
              borderRadius: 50,
              backgroundColor: theme ? '#3B82F6' : '#60A5FA',
              opacity: 0.2,
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Spinner color={theme ? '#3B82F6' : '#60A5FA'} />
            </View>
            <Text style={{ 
              color: theme ? '#1F2937' : '#F9FAFB',
              fontSize: 18,
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Loading work packages...
            </Text>
            <Text style={{ 
              color: theme ? '#6B7280' : '#9CA3AF',
              fontSize: 14,
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              Please wait while we fetch your project data
            </Text>
          </View>
        )}
      </Animatable.View>
    );

    // Enable/disable components for debugging (set to false to hide)
    const ENABLE_ADD_TASK_MODAL = true;  // Enable full functionality
    const ENABLE_MAIN_CONTENT = true;

    // Test all components together now that circular import is fixed
    const ENABLE_GANTT_NAVIGATION = true;  // RenderWorkPackage (confirmed working after circular import fix)
    const ENABLE_PROJECT_GANTT = true;     // ProjectGantt (confirmed working)
    const ENABLE_WORK_PACKAGE_LIST = true; // DraggableFlatList with RenderWorkPackage (should work now)

    // Component version control
    const USE_SAFE_COMPONENTS = false;     // Test complex components
    const USE_MINIMAL_COMPONENTS = false;  // Test actual complex components    // Simplified safe versions for debugging
    const safeGanttNavigationComponent = (
      <View style={{ flex: 1.9, width: 500, flexDirection: "row", backgroundColor: 'lightblue', padding: 20 }}>
        <Text style={{ color: 'black' }}>Safe Gantt Navigation</Text>
      </View>
    );

    const safeProjectGanttComponent = (
      <View style={{
        flex: 4,
        minHeight: "65%",
        padding: 40,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 16,
        marginBottom: 20,
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Text style={{ color: theme ? '#1F2937' : '#F9FAFB', fontSize: 18 }}>
          Safe Project Gantt Placeholder
        </Text>
        <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', marginTop: 10 }}>
          Work Packages: {retrievedWorkPackages?.length || 0}
        </Text>
        <Text style={{ color: theme ? '#6B7280' : '#9CA3AF' }}>
          Team Members: {projectParticipants?.length || 0}
        </Text>
      </View>
    );

    // Minimal versions to isolate the problematic component
    const minimalWorkPackageListComponent = (
      <View style={{
        top: projectGantt ? -20 : 0,
        minWidth: 400,
        maxHeight: maxHeightValue,
        flex: projectGantt ? 3 : 10,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 16,
        padding: 20
      }}>
        {Array.isArray(retrievedWorkPackages) && retrievedWorkPackages.length > 0 ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {retrievedWorkPackages.map((wp, index) => (
              <View key={wp._id || index} style={{
                width: 380,
                minHeight: 280,
                margin: 10,
                gap: 20,
                padding: 40,
                backgroundColor: theme ? '#ffffff' : '#374151',
                borderRadius: 20,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 20,
                elevation: 3,
                borderWidth: 1,
                borderColor: theme ? '#E5E7EB' : '#4B5563',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <View style={{
                  padding: 12,
                  borderRadius: 50,
                  backgroundColor: theme ? '#3B82F6' : '#60A5FA',
                  opacity: 0.2,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AntDesign
                    name="file-text"
                    size={24}
                    color={theme ? '#3B82F6' : '#60A5FA'}
                  />
                </View>
                <Text style={{ 
                  color: theme ? '#1F2937' : '#F9FAFB', 
                  fontSize: 18, 
                  fontWeight: 'bold',
                  textAlign: 'center',
                  maxWidth: 280
                }}>
                  {wp.work_package_title || 'Untitled Work Package'}
                </Text>
                <Text style={{ 
                  color: theme ? '#6B7280' : '#9CA3AF', 
                  fontSize: 12,
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  ID: {wp._id?.substring(0, 8)}...
                </Text>
                {wp.description && (
                  <Text style={{ 
                    color: theme ? '#6B7280' : '#9CA3AF', 
                    fontSize: 14,
                    textAlign: 'center',
                    maxWidth: 280,
                    lineHeight: 20
                  }}>
                    {wp.description.length > 100 ? wp.description.substring(0, 100) + '...' : wp.description}
                  </Text>
                )}
              </View>
            ))}
          </ScrollView>
        ) : (
          <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Text style={{ color: theme ? '#6B7280' : '#9CA3AF' }}>
              No work packages available
            </Text>
          </View>
        )}
      </View>
    );

    // Slot Booking Component
    const slotBookingComponent = (
      <View style={{
        flex: 1,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 16,
        padding: 20,
        marginTop: 10
      }}>
        <YStack gap={20}>
          {/* Header */}
          <XStack alignItems="center" justifyContent="space-between">
            <YStack>
              <H3 style={{ color: theme ? '#1F2937' : '#F9FAFB' }}>
                Slot Booking
              </H3>
              <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', fontSize: 14, marginTop: 4 }}>
                Schedule time slots for project activities
              </Text>
            </YStack>
            <Pressable
              onPress={() => Alert.alert('Add Slot', 'Create a new time slot booking')}
              style={{
                backgroundColor: theme ? '#3B82F6' : '#60A5FA',
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 12,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8
              }}
            >
              <AntDesign name="plus" size={16} color="white" />
              <Text style={{ color: 'white', fontWeight: '600' }}>
                New Slot
              </Text>
            </Pressable>
          </XStack>

          <Separator borderColor={theme ? '#E5E7EB' : '#4B5563'} />

          {/* Calendar Week View */}
          <YStack gap={12}>
            <Text style={{
              color: theme ? '#374151' : '#D1D5DB',
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 8
            }}>
              This Week
            </Text>

            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <XStack gap={12}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                  const date = new Date();
                  date.setDate(date.getDate() - date.getDay() + index + 1);
                  const dayNumber = date.getDate();
                  const isToday = new Date().getDate() === dayNumber;

                  return (
                    <Pressable
                      key={day}
                      style={({ pressed }) => ({
                        backgroundColor: isToday
                          ? (theme ? '#3B82F6' : '#60A5FA')
                          : pressed
                            ? (theme ? '#F3F4F6' : '#4B5563')
                            : (theme ? '#F9FAFB' : '#1F2937'),
                        padding: 16,
                        borderRadius: 12,
                        minWidth: 80,
                        alignItems: 'center',
                        borderWidth: 1,
                        borderColor: isToday
                          ? (theme ? '#3B82F6' : '#60A5FA')
                          : (theme ? '#E5E7EB' : '#4B5563')
                      })}
                    >
                      <Text style={{
                        color: isToday ? '#FFFFFF' : (theme ? '#6B7280' : '#9CA3AF'),
                        fontSize: 12,
                        fontWeight: '500',
                        marginBottom: 4
                      }}>
                        {day}
                      </Text>
                      <Text style={{
                        color: isToday ? '#FFFFFF' : (theme ? '#1F2937' : '#F9FAFB'),
                        fontSize: 20,
                        fontWeight: 'bold'
                      }}>
                        {dayNumber}
                      </Text>
                    </Pressable>
                  );
                })}
              </XStack>
            </ScrollView>
          </YStack>

          <Separator borderColor={theme ? '#E5E7EB' : '#4B5563'} />

          {/* Time Slots */}
          <YStack gap={12}>
            <Text style={{
              color: theme ? '#374151' : '#D1D5DB',
              fontSize: 16,
              fontWeight: '600'
            }}>
              Available Time Slots
            </Text>

            <ScrollView style={{ maxHeight: 400 }}>
              <YStack gap={12}>
                {[
                  { time: '09:00 - 10:00', title: 'Team Meeting', booked: true, participants: 5 },
                  { time: '10:00 - 11:00', title: 'Available', booked: false },
                  { time: '11:00 - 12:00', title: 'Client Call', booked: true, participants: 3 },
                  { time: '12:00 - 13:00', title: 'Lunch Break', booked: true, participants: 0 },
                  { time: '13:00 - 14:00', title: 'Available', booked: false },
                  { time: '14:00 - 15:00', title: 'Development Sprint', booked: true, participants: 8 },
                  { time: '15:00 - 16:00', title: 'Available', booked: false },
                  { time: '16:00 - 17:00', title: 'Code Review', booked: true, participants: 4 },
                  { time: '17:00 - 18:00', title: 'Available', booked: false },
                ].map((slot, index) => (
                  <Pressable
                    key={index}
                    onPress={() => {
                      if (!slot.booked) {
                        Alert.alert('Book Slot', `Would you like to book ${slot.time}?`, [
                          { text: 'Cancel', style: 'cancel' },
                          { text: 'Book', onPress: () => console.log('Booked:', slot.time) }
                        ]);
                      }
                    }}
                    style={({ pressed }) => ({
                      backgroundColor: slot.booked
                        ? (theme ? '#FEF3C7' : '#78350F')
                        : pressed
                          ? (theme ? '#DBEAFE' : '#1E3A8A')
                          : (theme ? '#F9FAFB' : '#1F2937'),
                      padding: 16,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: slot.booked
                        ? (theme ? '#FBBF24' : '#F59E0B')
                        : (theme ? '#E5E7EB' : '#4B5563'),
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    })}
                  >
                    <XStack alignItems="center" gap={12}>
                      <View style={{
                        backgroundColor: slot.booked
                          ? (theme ? '#F59E0B' : '#FBBF24')
                          : (theme ? '#10B981' : '#34D399'),
                        width: 4,
                        height: 40,
                        borderRadius: 2
                      }} />
                      <YStack>
                        <Text style={{
                          color: theme ? '#1F2937' : '#F9FAFB',
                          fontSize: 16,
                          fontWeight: '600'
                        }}>
                          {slot.time}
                        </Text>
                        <Text style={{
                          color: theme ? '#6B7280' : '#9CA3AF',
                          fontSize: 14,
                          marginTop: 2
                        }}>
                          {slot.title}
                        </Text>
                      </YStack>
                    </XStack>

                    {slot.booked ? (
                      <XStack alignItems="center" gap={6}>
                        {slot.participants > 0 && (
                          <>
                            <AntDesign
                              name="user"
                              size={14}
                              color={theme ? '#6B7280' : '#9CA3AF'}
                            />
                            <Text style={{
                              color: theme ? '#6B7280' : '#9CA3AF',
                              fontSize: 12
                            }}>
                              {slot.participants}
                            </Text>
                          </>
                        )}
                        <View style={{
                          backgroundColor: theme ? '#F59E0B' : '#FBBF24',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <Text style={{
                            color: 'white',
                            fontSize: 11,
                            fontWeight: '600'
                          }}>
                            BOOKED
                          </Text>
                        </View>
                      </XStack>
                    ) : (
                      <View style={{
                        backgroundColor: theme ? '#10B981' : '#34D399',
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 6
                      }}>
                        <Text style={{
                          color: 'white',
                          fontSize: 11,
                          fontWeight: '600'
                        }}>
                          AVAILABLE
                        </Text>
                      </View>
                    )}
                  </Pressable>
                ))}
              </YStack>
            </ScrollView>
          </YStack>

          {/* Quick Stats */}
          <XStack gap={12} marginTop={12}>
            <Card
              flex={1}
              backgroundColor={theme ? '#EBF4FF' : '#1E3A8A'}
              borderRadius={12}
              padding="$3"
            >
              <YStack alignItems="center">
                <Text style={{
                  color: theme ? '#3B82F6' : '#60A5FA',
                  fontSize: 24,
                  fontWeight: 'bold'
                }}>
                  5
                </Text>
                <Text style={{
                  color: theme ? '#3B82F6' : '#60A5FA',
                  fontSize: 12,
                  marginTop: 4
                }}>
                  Booked
                </Text>
              </YStack>
            </Card>

            <Card
              flex={1}
              backgroundColor={theme ? '#D1FAE5' : '#064E3B'}
              borderRadius={12}
              padding="$3"
            >
              <YStack alignItems="center">
                <Text style={{
                  color: theme ? '#10B981' : '#34D399',
                  fontSize: 24,
                  fontWeight: 'bold'
                }}>
                  4
                </Text>
                <Text style={{
                  color: theme ? '#10B981' : '#34D399',
                  fontSize: 12,
                  marginTop: 4
                }}>
                  Available
                </Text>
              </YStack>
            </Card>

            <Card
              flex={1}
              backgroundColor={theme ? '#FEE2E2' : '#7F1D1D'}
              borderRadius={12}
              padding="$3"
            >
              <YStack alignItems="center">
                <Text style={{
                  color: theme ? '#EF4444' : '#F87171',
                  fontSize: 24,
                  fontWeight: 'bold'
                }}>
                  20
                </Text>
                <Text style={{
                  color: theme ? '#EF4444' : '#F87171',
                  fontSize: 12,
                  marginTop: 4
                }}>
                  Total Slots
                </Text>
              </YStack>
            </Card>
          </XStack>
        </YStack>
      </View>
    );

    const minimalProjectGanttComponent = (
      <View style={{
        flex: 4,
        minHeight: "65%",
        padding: 40,
        backgroundColor: theme ? '#ffffff' : '#374151',
        borderRadius: 16,
        marginBottom: 20,
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Text style={{ color: theme ? '#1F2937' : '#F9FAFB', fontSize: 18 }}>
          Minimal Project Gantt (No ProjectGantt Component)
        </Text>
        <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', marginTop: 10 }}>
          This tests the container without the ProjectGantt component
        </Text>
      </View>
    );

    const minimalGanttNavigationComponent = (
      <View style={{ flex: 1.9, width: 500, flexDirection: "row", padding: 10 }}>
        <View style={{
          width: 40,
          height: 40,
          backgroundColor: 'lightblue',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 20
        }}>
          <Text>←</Text>
        </View>
        <View style={{
          flex: 1,
          backgroundColor: theme ? '#ffffff' : '#374151',
          borderRadius: 12,
          margin: 10,
          padding: 20,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Text style={{ color: theme ? '#1F2937' : '#F9FAFB' }}>
            Minimal Navigation (No RenderWorkPackage)
          </Text>
          {highlightedItem && (
            <Text style={{ color: theme ? '#6B7280' : '#9CA3AF', marginTop: 5 }}>
              {highlightedItem.work_package_title || 'Selected Item'}
            </Text>
          )}
        </View>
        <View style={{
          width: 40,
          height: 40,
          backgroundColor: 'lightblue',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 20
        }}>
          <Text>→</Text>
        </View>
      </View>
    );

    const mainContentComponent = (
      <View style={{
        flex: 10,
        minWidth: '100%',
        alignSelf: "stretch",
        flexDirection: "row",
        gap: 30,
        paddingBottom: 70,
      }}>
        {USE_SAFE_COMPONENTS ? (
          ENABLE_GANTT_NAVIGATION && safeGanttNavigationComponent
        ) : USE_MINIMAL_COMPONENTS ? (
          ENABLE_GANTT_NAVIGATION && minimalGanttNavigationComponent
        ) : (
          ENABLE_GANTT_NAVIGATION && ganttNavigationComponent
        )}
        <View style={{ flex: 3, flexDirection: 'column', paddingBottom: 30 }}>
          {USE_SAFE_COMPONENTS ? (
            <>
              {ENABLE_PROJECT_GANTT && safeProjectGanttComponent}
              {ENABLE_WORK_PACKAGE_LIST && (<Text>Safe Work Package List (not implemented)</Text>)}
            </>
          ) : USE_MINIMAL_COMPONENTS ? (
            <>
                {viewMode === 'booking' ? (
                  slotBookingComponent
                ) : (
                  <>
                      {ENABLE_PROJECT_GANTT && minimalProjectGanttComponent}
                      {ENABLE_WORK_PACKAGE_LIST && minimalWorkPackageListComponent}
                  </>
                )}
            </>
          ) : (
                <>
                  {viewMode === 'booking' ? (
                    slotBookingComponent
                  ) : (
                <YStack flex={1} gap={30} paddingBottom={30}>
                        {ENABLE_PROJECT_GANTT && projectGanttComponent}
                        {ENABLE_WORK_PACKAGE_LIST && workPackageListComponent}
                </YStack>
                  )}
                </>
          )}
        </View>
      </View>
    );

    // Modern Filter Bar Component
    const modernFilterBar = (
      <View style={{
        backgroundColor: theme ? '#FFFFFF' : '#1F2937',
        borderRadius: 12,
        padding: 16,
        marginHorizontal: 20,
        marginBottom: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        borderWidth: 1,
        borderColor: theme ? '#E5E7EB' : '#374151'
      }}>
        <Text style={{
          color: theme ? '#374151' : '#D1D5DB',
          fontSize: 16,
          fontWeight: '600'
        }}>
          Filter by Status:
        </Text>
        
        <XStack gap={8}>
          {(['all', 'active', 'completed'] as const).map((filter) => (
            <Pressable
              key={filter}
              onPress={() => handleFilterChange(filter)}
              style={({ pressed }) => ({
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 20,
                backgroundColor: filterStatus === filter 
                  ? (theme ? '#3B82F6' : '#60A5FA')
                  : pressed 
                    ? (theme ? '#F3F4F6' : '#4B5563')
                    : 'transparent',
                borderWidth: 1,
                borderColor: filterStatus === filter 
                  ? (theme ? '#3B82F6' : '#60A5FA')
                  : theme ? '#D1D5DB' : '#6B7280'
              })}
            >
              <Text style={{
                color: filterStatus === filter 
                  ? '#FFFFFF'
                  : theme ? '#6B7280' : '#9CA3AF',
                fontSize: 14,
                fontWeight: '500',
                textTransform: 'capitalize'
              }}>
                {filter}
              </Text>
            </Pressable>
          ))}
        </XStack>
      </View>
    );

    // Modern Project Status Header
    const projectStatusHeader = (
      <View style={{
        backgroundColor: theme ? '#FFFFFF' : '#1F2937',
        borderRadius: 16,
        padding: 20,
        margin: 20,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
        borderWidth: 1,
        borderColor: theme ? '#E5E7EB' : '#374151'
      }}>
        <XStack justifyContent="space-between" alignItems="center" marginBottom={16}>
          <H4 style={{ color: theme ? '#1F2937' : '#F9FAFB' }}>
            Project Overview
          </H4>
          
          <View style={{
            backgroundColor: theme ? '#DBEAFE' : '#1E3A8A',
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 20
          }}>
            <Text style={{
              color: theme ? '#2563EB' : '#60A5FA',
              fontSize: 12,
              fontWeight: '600'
            }}>
              {retrievedWorkPackages.length} Work Packages
            </Text>
          </View>
        </XStack>

        {/* Progress Bar */}
        <View style={{ marginBottom: 16 }}>
          <Text style={{ 
            color: theme ? '#6B7280' : '#9CA3AF',
            fontSize: 14,
            marginBottom: 8
          }}>
            Overall Progress
          </Text>
          <View style={{
            width: '100%',
            height: 8,
            backgroundColor: theme ? '#F3F4F6' : '#374151',
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: 4
          }}>
            <View style={{
              width: `${Math.min((retrievedWorkPackages.length / 5) * 100, 100)}%`,
              height: '100%',
              backgroundColor: theme ? '#3B82F6' : '#60A5FA',
              borderRadius: 4
            }} />
          </View>
          <Text style={{ 
            color: theme ? '#6B7280' : '#9CA3AF',
            fontSize: 12,
            textAlign: 'right'
          }}>
            {Math.min((retrievedWorkPackages.length / 5) * 100, 100).toFixed(0)}% Complete
          </Text>
        </View>

        {/* Quick Stats */}
        <XStack gap={16} justifyContent="space-around">
          <View style={{ alignItems: 'center', flex: 1 }}>
            <Text style={{ 
              color: theme ? '#059669' : '#34D399',
              fontSize: 18,
              fontWeight: 'bold'
            }}>
              {retrievedWorkPackages.filter(wp => wp.status === 'completed').length}
            </Text>
            <Text style={{ 
              color: theme ? '#6B7280' : '#9CA3AF',
              fontSize: 12
            }}>
              Completed
            </Text>
          </View>
          
          <View style={{ alignItems: 'center', flex: 1 }}>
            <Text style={{ 
              color: theme ? '#DC2626' : '#F87171',
              fontSize: 18,
              fontWeight: 'bold'
            }}>
              {retrievedWorkPackages.filter(wp => wp.status === 'in_progress').length}
            </Text>
            <Text style={{ 
              color: theme ? '#6B7280' : '#9CA3AF',
              fontSize: 12
            }}>
              In Progress
            </Text>
          </View>
          
          <View style={{ alignItems: 'center', flex: 1 }}>
            <Text style={{ 
              color: theme ? '#D97706' : '#FCD34D',
              fontSize: 18,
              fontWeight: 'bold'
            }}>
              {retrievedWorkPackages.filter(wp => wp.status === 'pending').length}
            </Text>
            <Text style={{ 
              color: theme ? '#6B7280' : '#9CA3AF',
              fontSize: 12
            }}>
              Pending
            </Text>
          </View>
        </XStack>
      </View>
    );

    // Modern Action Toolbar Component
    const modernActionToolbar = (
      <View style={{
        position: 'absolute',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center'
      }}>
        {/* View Mode Selector */}
        <View style={{
          flexDirection: 'row',
          backgroundColor: theme ? '#FFFFFF' : '#374151',
          borderRadius: 16,
          padding: 4,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 12,
          elevation: 8,
          borderWidth: 1,
          borderColor: theme ? '#E5E7EB' : '#4B5563'
        }}>
          {(['kanban', 'gantt', 'list', 'booking'] as const).map((mode) => (
            <Pressable
              key={mode}
              onPress={() => handleViewModeChange(mode)}
              style={({ pressed }) => ({
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 12,
                backgroundColor: viewMode === mode 
                  ? (theme ? '#3B82F6' : '#60A5FA')
                  : pressed 
                    ? (theme ? '#F3F4F6' : '#4B5563')
                    : 'transparent'
              })}
            >
              <AntDesign
                name={
                  mode === 'kanban' ? 'appstore' :
                    mode === 'gantt' ? 'bar-chart' :
                      mode === 'list' ? 'bars' :
                        'calendar'
                }
                size={16}
                color={viewMode === mode 
                  ? '#FFFFFF' 
                  : theme ? '#6B7280' : '#9CA3AF'
                }
              />
            </Pressable>
          ))}
        </View>

        {/* Main Action Button */}
        <Pressable
          onPress={handleProjectSettings}
          style={({ pressed }) => ({
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: pressed 
              ? (theme ? '#2563EB' : '#3B82F6')
              : (theme ? '#3B82F6' : '#60A5FA'),
            justifyContent: 'center',
            alignItems: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 12
          })}
        >
          <AntDesign name="plus" size={24} color="white" />
        </Pressable>
      </View>
    );

    // Modern Action Sheet Component
    const modernActionSheet = (
      <Sheet 
        modal 
        open={showActionSheet} 
        onOpenChange={setShowActionSheet}
        snapPoints={[300]}
        dismissOnSnapToBottom
      >
        <Sheet.Overlay />
        <Sheet.Frame
          backgroundColor={theme ? '#FFFFFF' : '#1F2937'}
          borderTopLeftRadius={24}
          borderTopRightRadius={24}
          padding={20}
        >
          <Sheet.Handle backgroundColor={theme ? '#E5E7EB' : '#4B5563'} />
          
          <H4 style={{ 
            color: theme ? '#1F2937' : '#F9FAFB', 
            marginBottom: 20,
            textAlign: 'center'
          }}>
            Project Actions
          </H4>

          <YStack gap={12}>
            <TamaguiButton
              onPress={() => {
                setShowActionSheet(false);
                handleAddTeamMember();
              }}
              backgroundColor={theme ? '#EBF4FF' : '#1E3A8A'}
              color={theme ? '#2563EB' : '#60A5FA'}
              borderRadius={12}
              height={48}
              justifyContent="flex-start"
              paddingLeft={16}
              icon={<MaterialIcons name="group-add" size={20} color={theme ? '#2563EB' : '#60A5FA'} />}
            >
              Add Team Member
            </TamaguiButton>

            <TamaguiButton
              onPress={() => {
                setShowActionSheet(false);
                handleShareProject();
              }}
              backgroundColor={theme ? '#F0FDF4' : '#14532D'}
              color={theme ? '#16A34A' : '#4ADE80'}
              borderRadius={12}
              height={48}
              justifyContent="flex-start"
              paddingLeft={16}
              icon={<AntDesign name="share-alt" size={20} color={theme ? '#16A34A' : '#4ADE80'} />}
            >
              Share Project
            </TamaguiButton>

            <TamaguiButton
              onPress={() => {
                setShowActionSheet(false);
                handleExportProject();
              }}
              backgroundColor={theme ? '#FEF3C7' : '#92400E'}
              color={theme ? '#D97706' : '#FCD34D'}
              borderRadius={12}
              height={48}
              justifyContent="flex-start"
              paddingLeft={16}
              icon={<Feather name="download" size={20} color={theme ? '#D97706' : '#FCD34D'} />}
            >
              Export Project
            </TamaguiButton>

            <TamaguiButton
              onPress={() => {
                setShowActionSheet(false);
                wpRef.current?.openModal();
              }}
              backgroundColor={theme ? '#EDE9FE' : '#581C87'}
              color={theme ? '#7C3AED' : '#A855F7'}
              borderRadius={12}
              height={48}
              justifyContent="flex-start"
              paddingLeft={16}
              icon={<AntDesign name="plus" size={20} color={theme ? '#7C3AED' : '#A855F7'} />}
            >
              Add Work Package
            </TamaguiButton>
          </YStack>
        </Sheet.Frame>
      </Sheet>
    );

    return (
      <DefaultScreen
        fetchingState={isFetching}
        removeMargin
        style={{ paddingTop: 0, paddingBottom: 20 }}
        containerStyle={{ paddingBottom: 70 }}
        layoutCallback={setPageDimensions}
        scrollDisabled
      >
        <AddWorkPackage
          ref={wpRef}
          callback={_addWP}
          projectId={projectId}
          projectTitle={projectDataTyped?.data?.title || title || ''}
        />
        <>
          {ENABLE_ADD_TASK_MODAL && addTaskModalComponent}

          {/* Modern Project Status Header */}
          {!isLoading && projectStatusHeader}

          {/* Modern Filter Bar */}
          {!isLoading && retrievedWorkPackages.length > 0 && modernFilterBar}

          {isLoading ? loadingComponent : (
            ENABLE_MAIN_CONTENT && mainContentComponent
          )}

          {/* Modern UI Components */}
          {modernActionToolbar}
          {modernActionSheet}

          {/* Team Modal */}
          <Modal
            isVisible={showTeamModal}
            onBackdropPress={() => setShowTeamModal(false)}
            style={{ margin: 0, justifyContent: 'center' }}
          >
            <View style={{
              backgroundColor: theme ? '#FFFFFF' : '#1F2937',
              margin: 20,
              borderRadius: 20,
              padding: 20,
              maxHeight: '80%'
            }}>
              <View style={{ 
                flexDirection: 'row', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 20
              }}>
                <H4 style={{ color: theme ? '#1F2937' : '#F9FAFB' }}>
                  Team Members
                </H4>
                <Pressable
                  onPress={() => setShowTeamModal(false)}
                  style={{
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: theme ? '#F3F4F6' : '#374151'
                  }}
                >
                  <AntDesign name="close" size={16} color={theme ? '#6B7280' : '#9CA3AF'} />
                </Pressable>
              </View>
              
              <AddTeamMember 
                projectId={projectId}
                onSuccess={() => {
                  setShowTeamModal(false);
                  refetch();
                }}
              />
            </View>
          </Modal>

          {/* Share Modal */}
          <Modal
            isVisible={showShareModal}
            onBackdropPress={() => setShowShareModal(false)}
            style={{ margin: 0, justifyContent: 'center' }}
          >
            <View style={{
              backgroundColor: theme ? '#FFFFFF' : '#1F2937',
              margin: 20,
              borderRadius: 20,
              padding: 20
            }}>
              <View style={{ 
                flexDirection: 'row', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 20
              }}>
                <H4 style={{ color: theme ? '#1F2937' : '#F9FAFB' }}>
                  Share Project
                </H4>
                <Pressable
                  onPress={() => setShowShareModal(false)}
                  style={{
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: theme ? '#F3F4F6' : '#374151'
                  }}
                >
                  <AntDesign name="close" size={16} color={theme ? '#6B7280' : '#9CA3AF'} />
                </Pressable>
              </View>
              
              <YStack gap={16}>
                <Text style={{ 
                  color: theme ? '#6B7280' : '#9CA3AF',
                  textAlign: 'center'
                }}>
                  Share this project with your team or generate a public link
                </Text>
                
                <TamaguiButton
                  backgroundColor={theme ? '#3B82F6' : '#60A5FA'}
                  color="white"
                  borderRadius={12}
                  height={48}
                  onPress={() => {
                    // TODO: Implement copy link functionality
                    Alert.alert('Success', 'Project link copied to clipboard!');
                    setShowShareModal(false);
                  }}
                >
                  Copy Project Link
                </TamaguiButton>
                
                <TamaguiButton
                  backgroundColor={theme ? '#10B981' : '#34D399'}
                  color="white"
                  borderRadius={12}
                  height={48}
                  onPress={() => {
                    // TODO: Implement email sharing
                    Alert.alert('Feature Coming Soon', 'Email sharing will be available soon!');
                    setShowShareModal(false);
                  }}
                >
                  Share via Email
                </TamaguiButton>
              </YStack>
            </View>
          </Modal>
        </>
    </DefaultScreen>
  )
  
  } catch (error) {
    debugLog('Component', 'Critical error occurred', { error: error.message, stack: error.stack });
    console.error('IndividualProjectPage crashed:', error);
    
    // Return a safe fallback UI
    return (
      <DefaultScreen style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ padding: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 10, color: 'red' }}>
            Something went wrong
          </Text>
          <Text style={{ fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
            {error?.message || 'An unexpected error occurred'}
          </Text>
          <Button 
            buttonText="Retry" 
            onPress={() => {
              debugLog('ErrorBoundary', 'Retry button pressed');
              navigation.goBack();
            }} 
          />
        </View>
      </DefaultScreen>
    );
  }
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(IndividualProjectPage);